/**
 * PeerPicker -- modal that lists hostable peers (LLM bots + chat workers).
 * Two-step new-chat flow: ConversationSidebar's `new-chat-btn` opens this
 * modal; user picks a peer; parent creates the conversation.
 *
 * Platform-agnostic: no `window` / `document` references; pure DI props.
 */

import { canHost } from 'peer-sdk'
import { BotIcon } from './icons.tsx'
import { PARTICIPANT_COLORS, hashString } from './participantColors.js'

export const PeerPicker = ({
  peers = [],
  onSelect,
  onClose,
}: {
  peers: any[]
  onSelect: (peer: any) => void
  onClose: () => void
}) => {
  const hostable = peers.filter((p: any) => canHost(p))

  return (
    <div
      className="peer-picker-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 900,
      }}
    >
      <div
        className="peer-picker"
        data-testid="peer-picker"
        onClick={(e: any) => e.stopPropagation()}
        style={{
          background: 'var(--bg-secondary)',
          padding: 'var(--spacing-lg)',
          borderRadius: 'var(--radius-lg)',
          minWidth: '320px',
          maxWidth: '480px',
          maxHeight: '70vh',
          overflowY: 'auto',
          color: 'var(--text-primary)',
        }}
      >
        <div
          style={{
            fontWeight: 600,
            marginBottom: 'var(--spacing-md)',
          }}
        >
          Start a new chat
        </div>
        {hostable.length === 0 ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)' }}>
            No bots or chat workers available. Create a bot first.
          </div>
        ) : (
          <div className="peer-picker-list">
            {hostable.map((p: any) => {
              const color = PARTICIPANT_COLORS[hashString(p.roleId) % PARTICIPANT_COLORS.length]
              return (
                <button
                  key={p.roleId}
                  type="button"
                  className="peer-picker-item"
                  onClick={() => onSelect(p)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    width: '100%',
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    marginBottom: 'var(--spacing-xs)',
                    background: 'transparent',
                    border: '1px solid var(--border-color, #333)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    color: 'var(--text-primary)',
                    textAlign: 'left',
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${color}22, ${color}11)`,
                    }}
                  >
                    <BotIcon color={color} size={16} />
                  </span>
                  <span style={{ fontWeight: 500 }}>{p.displayName}</span>
                  {p.description && (
                    <span
                      style={{
                        color: 'var(--text-dim)',
                        fontSize: 'var(--font-size-sm)',
                        marginLeft: 'auto',
                      }}
                    >
                      {p.description}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
